<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import TeamExportsAPI from 'dashboard/api/teamExports';
import ReportHeader from './components/ReportHeader.vue';
import ReportFilters from './components/ReportFilters.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const DATE_FORMAT = 'yyyy-MM-dd';

const { t } = useI18n();

const teams = ref([]);
const isLoadingTeams = ref(true);
const teamId = ref('');
const since = ref('');
const until = ref('');
const downloading = ref('');

const teamOptions = computed(() =>
  teams.value.map(team => ({ value: team.id, label: team.name }))
);

const canDownload = computed(
  () => !!teamId.value && !!since.value && !downloading.value
);

// El selector de Informes emite inicio/fin de día en unix (hora local); el
// backend recibe días naturales y la zona horaria del navegador.
const onFilterChange = ({ from, to }) => {
  since.value = format(fromUnixTime(from), DATE_FORMAT);
  until.value = format(fromUnixTime(to), DATE_FORMAT);
};

const fileNameFrom = headers =>
  decodeURIComponent(
    (headers['content-disposition'] || '').match(
      /filename\*=UTF-8''([^;]+)/
    )?.[1] || ''
  );

const download = async kind => {
  downloading.value = kind;
  try {
    const { data, headers } = await TeamExportsAPI.download({
      teamId: teamId.value,
      kind,
      since: since.value,
      until: until.value,
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(data);
    link.download = fileNameFrom(headers) || `${kind}.xlsx`;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch {
    useAlert(t('TEAM_EXPORT.ERROR'));
  } finally {
    downloading.value = '';
  }
};

onMounted(async () => {
  try {
    const { data } = await TeamExportsAPI.get();
    teams.value = data;
    if (data.length === 1) teamId.value = data[0].id;
  } finally {
    isLoadingTeams.value = false;
  }
});
</script>

<template>
  <ReportHeader
    :header-title="$t('TEAM_EXPORT.HEADER')"
    :header-description="$t('TEAM_EXPORT.DESCRIPTION')"
  />

  <div class="flex flex-col gap-6 pb-6">
    <ReportFilters
      :show-entity-filter="false"
      :show-group-by="false"
      :show-business-hours="false"
      @filter-change="onFilterChange"
    />

    <p
      v-if="!isLoadingTeams && !teams.length"
      class="mb-0 text-body-main text-n-slate-11"
    >
      {{ $t('TEAM_EXPORT.NO_TEAMS') }}
    </p>

    <div
      v-else
      class="flex flex-col gap-5 rounded-xl border border-n-weak bg-n-background p-5"
    >
      <div class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('TEAM_EXPORT.TEAM') }}
        </span>
        <Select
          v-model="teamId"
          :options="teamOptions"
          :placeholder="$t('TEAM_EXPORT.TEAM_PLACEHOLDER')"
          :disabled="isLoadingTeams"
        />
      </div>

      <p class="mb-0 text-body-small text-n-slate-11">
        {{ $t('TEAM_EXPORT.SCOPE_HINT') }}
      </p>

      <div class="flex flex-wrap gap-3">
        <Button
          icon="i-lucide-messages-square"
          :label="$t('TEAM_EXPORT.DOWNLOAD_CONVERSATIONS')"
          :disabled="!canDownload"
          :is-loading="downloading === 'conversations'"
          @click="download('conversations')"
        />
        <Button
          icon="i-lucide-message-square-text"
          :label="$t('TEAM_EXPORT.DOWNLOAD_MESSAGES')"
          faded
          :disabled="!canDownload"
          :is-loading="downloading === 'messages'"
          @click="download('messages')"
        />
      </div>
    </div>
  </div>
</template>
